import type { Component } from 'vue'
import { ref, watch } from 'vue'

interface DraggableComponent {
  id: string
  component: Component
  props?: Record<string, unknown>
  events?: Record<string, unknown>
}

export function useComponentOrder(pageId: string, defaultComponents: DraggableComponent[]) {
  const components = ref<DraggableComponent[]>([])
  const storageKey = `component-order-${pageId}`

  // Charger l'ordre sauvegardé au démarrage
  const loadSavedOrder = () => {
    const savedOrder = localStorage.getItem(storageKey)
    if (savedOrder) {
      try {
        const savedIds = JSON.parse(savedOrder)
        // Restaurer l'ordre en utilisant les composants par défaut
        const savedComponents = savedIds
          .map((id: string) => {
            const defaultComponent = defaultComponents.find((comp) => comp.id === id)
            return defaultComponent || null
          })
          .filter(Boolean)

        // Ajouter les nouveaux composants qui ne sont pas dans l'ordre sauvegardé
        const newComponents = defaultComponents.filter((comp) => !savedIds.includes(comp.id))

        components.value = [...savedComponents, ...newComponents]
      } catch (e) {
        console.error("Erreur lors du chargement de l'ordre des composants:", e)
        components.value = defaultComponents
      }
    } else {
      components.value = defaultComponents
    }
  }

  // Sauvegarder l'ordre quand il change
  const saveOrder = (newOrder: DraggableComponent[]) => {
    try {
      // Ne sauvegarder que les IDs des composants
      const componentIds = newOrder.map((item) => item.id)
      localStorage.setItem(storageKey, JSON.stringify(componentIds))
    } catch (e) {
      console.error("Erreur lors de la sauvegarde de l'ordre des composants:", e)
    }
  }

  // Surveiller les changements d'ordre
  watch(
    components,
    (newOrder) => {
      saveOrder(newOrder)
    },
    { deep: true },
  )

  // Charger l'ordre au démarrage
  loadSavedOrder()

  return {
    components,
    loadSavedOrder,
    saveOrder,
  }
}
